import {
  ABILITIES,
  ARENA_HEIGHT,
  ARENA_WIDTH,
  BOSS_START_MS,
  FRENZY_XP_MULTIPLIER,
  MAX_PLAYERS,
  PLAYER_IDLE_TIMEOUT_MS,
  REVIVE_DURATION_MS,
  type AbilityId,
} from "./constants";
import type { BossPhase, LootId, MatchStage, PowerUpKind } from "./protocol";

export const TEAM_POWER_MAX_STACKS = 15;
export const TEAM_COOLDOWN_MAX_STACKS = 12;
export const TEAM_HP_MAX_STACKS = 15;
export const TEAM_MOVE_MAX_STACKS = 10;
export const HAZARD_DAMAGE_GRACE_MS = 350;

const FOOD_POWER_UPS: ReadonlySet<PowerUpKind> = new Set([
  "sea_bass",
  "energy_bar",
  "ice_pop",
  "canteen",
]);

export function isFoodPowerUpKind(kind: PowerUpKind): boolean {
  return FOOD_POWER_UPS.has(kind);
}

export function foodSupplyLimitForPlayers(playerCount: number): number {
  return Math.max(2, Math.min(6, Math.ceil(Math.max(1, playerCount) / 12) + 1));
}

export function temporaryMoveSpeedMultiplier(
  hasteActive: boolean,
  energyBarActive: boolean,
  rodActive: boolean,
): number {
  const combined =
    (hasteActive ? 1.35 : 1) *
    (energyBarActive ? 1.28 : 1) *
    (rodActive ? 1.85 : 1);
  return Math.min(2.15, combined);
}

export function enemySlowMultiplier(
  chilledByFieldGear: boolean,
  chilledByIcePop: boolean,
): number {
  return chilledByFieldGear ? 0.55 : chilledByIcePop ? 0.7 : 1;
}

export function hazardDamageMultiplier(canteenActive: boolean): number {
  return canteenActive ? 0.45 : 1;
}

interface UpgradeStats {
  power: number;
  cooldown: number;
  moveSpeed: number;
  range: number;
  multicast: number;
  armor: number;
  regen: number;
  critChance: number;
  executeThreshold: number;
  lifesteal: number;
  specialCooldown: number;
  pickupRadius: number;
  berserker: number;
}

interface RaidUpgradeStats {
  teamPowerStacks: number;
  teamCooldownStacks: number;
  teamMaxHpStacks: number;
  teamMoveSpeedStacks: number;
}

export function parseAbility(value: string | null): AbilityId {
  if (ABILITIES.includes(value as AbilityId)) return value as AbilityId;
  return "ember";
}

export function sanitizeName(value: string): string {
  const clean = value
    .replace(/[^\p{L}\p{N} _-]/gu, "")
    .trim()
    .slice(0, 18);
  return clean || "Survivor";
}

export function xpForLevel(level: number): number {
  const safeLevel = Math.max(1, level);
  return 60 + safeLevel * 35 + safeLevel * safeLevel * 5;
}

export function upgradeChoiceCooldownMs(roundNumber: number): number {
  return Math.max(1, Math.floor(roundNumber)) >= 2 ? 8_000 : 3_000;
}

export function stageForElapsed(
  elapsed: number,
  fastMode = false,
): MatchStage {
  if (elapsed >= (fastMode ? 10_000 : BOSS_START_MS)) return "boss";
  if (elapsed >= (fastMode ? 7_000 : 5 * 60_000)) return "chaos";
  if (elapsed >= (fastMode ? 3_500 : 2 * 60_000)) return "escalation";
  return "descent";
}

export function firstArmyWaveAt(now: number, fastMode = false): number {
  return now + (fastMode ? 6_000 : 55_000);
}

const ANKYLOSAURUS_CONTACT_RADIUS_X = 84;
const ANKYLOSAURUS_CONTACT_RADIUS_Y = 48;
const ANKYLOSAURUS_CONTACT_OFFSET_Y = -12;

export function ankylosaurusTouchesPlayer(
  player: { x: number; y: number },
  ankylosaurus: { x: number; y: number },
): boolean {
  // The sprite is long and low, and is rendered slightly above its server
  // position. These radii include the player's 18px body without reviving the
  // oversized square collision area used by the old gelatinous-cube art.
  const normalizedX =
    (player.x - ankylosaurus.x) / ANKYLOSAURUS_CONTACT_RADIUS_X;
  const normalizedY =
    (player.y -
      (ankylosaurus.y + ANKYLOSAURUS_CONTACT_OFFSET_Y)) /
    ANKYLOSAURUS_CONTACT_RADIUS_Y;
  return normalizedX * normalizedX + normalizedY * normalizedY <= 1;
}

export function moteDropsAllowedForElapsed(
  elapsed: number,
  bossStartMs = BOSS_START_MS,
): boolean {
  return elapsed >= 0 && elapsed < bossStartMs;
}

export function enemyPopulationScaleForRound(roundNumber: number): number {
  const safeRound = Math.max(1, Math.floor(roundNumber));
  if (safeRound >= 4) return 2;
  if (safeRound >= 2) return 1.5;
  return 1;
}

export function enemyHealthScaleForRound(roundNumber: number): number {
  const safeRound = Math.max(1, Math.floor(roundNumber));
  if (safeRound >= 4) return 2.5;
  if (safeRound === 3) return 2;
  if (safeRound === 2) return 1.5;
  return 1;
}

export function enemyPopulationTarget(
  playerCount: number,
  progress: number,
  roundNumber: number,
): number {
  const safePlayers = Math.max(1, Math.floor(playerCount));
  const safeProgress = clamp(progress, 0, 1.35);
  const baseTarget =
    8 +
    safePlayers * 1.35 +
    safeProgress * (18 + safePlayers * 1.65);
  return Math.min(
    360,
    Math.floor(baseTarget * enemyPopulationScaleForRound(roundNumber)),
  );
}

export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function round(value: number, precision = 1): number {
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
}

export function arenaBoundsForPlayers(
  playerCount: number,
): [x: number, y: number, width: number, height: number] {
  const safeCount = clamp(Math.floor(playerCount), 1, MAX_PLAYERS);
  const population = (safeCount - 1) / Math.max(1, MAX_PLAYERS - 1);
  const scale = 0.625 + Math.sqrt(population) * 0.375;
  const width = Math.round(ARENA_WIDTH * scale);
  const height = Math.round(ARENA_HEIGHT * scale);
  return [
    (ARENA_WIDTH - width) / 2,
    (ARENA_HEIGHT - height) / 2,
    width,
    height,
  ];
}

export function reviveProgress(startedAt: number, now: number): number {
  if (startedAt <= 0 || now <= startedAt) return 0;
  return clamp((now - startedAt) / REVIVE_DURATION_MS, 0, 1);
}

export function reviveShieldActive(shieldUntil: number, now: number): boolean {
  return shieldUntil > now;
}

export function lootProtectionActive(
  hasPendingLoot: boolean,
  ghost: boolean,
): boolean {
  return hasPendingLoot && !ghost;
}

export function personalChallengeRefreshReady(
  completed: boolean,
  refreshAt: number,
  now: number,
): boolean {
  return completed && refreshAt > 0 && now >= refreshAt;
}

export function playerIdleTimeoutReached(
  stationarySince: number,
  now: number,
  timeoutMs = PLAYER_IDLE_TIMEOUT_MS,
): boolean {
  return (
    stationarySince > 0 &&
    now >= stationarySince &&
    now - stationarySince >= timeoutMs
  );
}

export function untargetedEnemyDestination(
  enemyId: number,
  now: number,
  arena = arenaBoundsForPlayers(MAX_PLAYERS),
): { x: number; y: number } {
  const [arenaX, arenaY, arenaWidth, arenaHeight] = arena;
  const phase = enemyId * 2.39996 + now / 5_200;
  const radiusX = arenaWidth * (0.16 + (enemyId % 5) * 0.025);
  const radiusY = arenaHeight * (0.18 + (enemyId % 4) * 0.03);
  return {
    x: arenaX + arenaWidth / 2 + Math.cos(phase) * radiusX,
    y: arenaY + arenaHeight / 2 + Math.sin(phase * 1.17) * radiusY,
  };
}

export function sessionPlayerIsReclaimable(
  connected: boolean,
  disconnectedAt: number,
  now: number,
): boolean {
  return (
    !connected &&
    disconnectedAt > 0 &&
    now - disconnectedAt <= 20_000
  );
}

export function consumeUltimateBarrageCharge(
  charges: number,
  now: number,
  cooldownMs: number,
): { remaining: number; readyAt: number } {
  const remaining = Math.max(0, Math.floor(charges) - 1);
  return {
    remaining,
    readyAt: remaining > 0 ? now : now + Math.max(0, cooldownMs),
  };
}

export function partyWiped(
  players: ReadonlyArray<{ connected: boolean; ghost: boolean }>,
): boolean {
  const connectedPlayers = players.filter((player) => player.connected);
  return (
    connectedPlayers.length > 0 &&
    connectedPlayers.every((player) => player.ghost)
  );
}

export function soloRecoveryPending(
  players: ReadonlyArray<{
    connected: boolean;
    ghost: boolean;
    deaths: number;
    respawnAt: number;
  }>,
  now: number,
): boolean {
  const connectedPlayers = players.filter((player) => player.connected);
  if (connectedPlayers.length !== 1) return false;
  const [player] = connectedPlayers;
  return (
    player.ghost &&
    player.deaths === 1 &&
    player.respawnAt > now
  );
}

export function frenzyXpMultiplier(overdriveUntil: number, now: number): number {
  return overdriveUntil > now ? FRENZY_XP_MULTIPLIER : 1;
}

export function moteGoalForPlayers(playerCount: number, fastMode = false): number {
  const safePlayers = Math.max(1, Math.min(75, Math.floor(playerCount)));
  if (fastMode) return 4 + Math.ceil(safePlayers * 0.05);
  return Math.ceil(14 + 4 * safePlayers ** 0.82);
}

export function triangleZoneCount(playerCount: number): number {
  const safePlayers = Math.max(0, Math.floor(playerCount));
  if (safePlayers < 2) return 0;
  return safePlayers === 2 ? 2 : 3;
}

export function teamComboRequiredPlayers(playerCount: number): number {
  return Math.max(1, Math.floor(playerCount)) === 1 ? 1 : 2;
}

export function armyWaveHitsPoint(
  point: { x: number; y: number },
  wallPosition: number,
  vertical: boolean,
  gapCenter: number,
  gapSize: number,
  wallWidth: number,
): boolean {
  const crossingDistance = Math.abs(
    (vertical ? point.x : point.y) - wallPosition,
  );
  const gapDistance = Math.abs(
    (vertical ? point.y : point.x) - gapCenter,
  );
  return crossingDistance <= wallWidth / 2 && gapDistance > gapSize / 2;
}

export function bossPhaseForHealthRatio(healthRatio: number): BossPhase {
  if (healthRatio <= 0.25) return 4;
  if (healthRatio <= 0.5) return 3;
  if (healthRatio <= 0.75) return 2;
  return 1;
}

export function raidUltimateRequiredVotes(playerCount: number): number {
  return Math.max(1, Math.floor(Math.max(0, playerCount) / 2) + 1);
}

export function isAbilityReaction(a: AbilityId, b: AbilityId): boolean {
  if (a === b) return false;
  const pair = new Set<AbilityId>([a, b]);
  return (
    (pair.has("frost") && pair.has("solar")) ||
    (pair.has("void") && pair.has("chain")) ||
    (pair.has("bard") && pair.has("necro"))
  );
}

export function oneWayLaserHitsPoint(
  point: { x: number; y: number },
  origin: { x: number; y: number },
  angle: number,
  length: number,
  halfWidth: number,
): boolean {
  const dx = point.x - origin.x;
  const dy = point.y - origin.y;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const along = cos * dx + sin * dy;
  const perpendicular = Math.abs(-sin * dx + cos * dy);
  return along >= 0 && along <= length && perpendicular <= halfWidth;
}

export function hazardDamageAllowed(graceUntil: number, now: number): boolean {
  return now >= graceUntil;
}

export function bossAttackCadenceMs(
  pressure: number,
  phase: BossPhase,
): number {
  const phaseSpeed = phase === 4 ? 320 : phase === 3 ? 180 : 0;
  return Math.max(1_650, 2_250 - clamp(pressure, 0, 1) * 700 - phaseSpeed);
}

export function bossPatrolPosition(
  elapsedMs: number,
  centerX: number,
  centerY: number,
): { x: number; y: number } {
  const elapsed = Math.max(0, elapsedMs);
  return {
    x: centerX + Math.sin(elapsed / 6_000) * 120,
    y: centerY + Math.sin(elapsed / 9_000) * 70,
  };
}

export function teamPowerMultiplierForStacks(stacks: number): number {
  return 1 + clamp(Math.floor(stacks), 0, TEAM_POWER_MAX_STACKS) * 0.02;
}

export function teamCooldownMultiplierForStacks(stacks: number): number {
  return 1 - clamp(Math.floor(stacks), 0, TEAM_COOLDOWN_MAX_STACKS) * 0.02;
}

export function teamMoveMultiplierForStacks(stacks: number): number {
  return 1 + clamp(Math.floor(stacks), 0, TEAM_MOVE_MAX_STACKS) * 0.015;
}

export function necromancerMulticastLimits(multicast: number): {
  ownerLimit: number;
  raiseCount: number;
} {
  const stacks = clamp(Math.floor(multicast), 0, 4);
  return {
    ownerLimit: 2 + stacks,
    raiseCount: 1 + Math.ceil(stacks / 2),
  };
}

export function regularLootAvailable(
  id: LootId,
  player: UpgradeStats,
  raid: RaidUpgradeStats,
): boolean {
  switch (id) {
    case "power":
      return player.power < 3.5;
    case "haste":
      return player.cooldown > 0.48;
    case "vitality":
      return true;
    case "fleet":
      return player.moveSpeed < 1.6;
    case "reach":
      return player.range < 2.1;
    case "multicast":
      return player.multicast < 4;
    case "armor":
      return player.armor < 0.48;
    case "renewal":
      return player.regen < 9;
    case "lucky_strike":
      return player.critChance < 0.4;
    case "executioner":
      return player.executeThreshold < 0.24;
    case "blood_coin":
      return player.lifesteal < 0.04;
    case "ultimate_haste":
      return player.specialCooldown > 0.52;
    case "magnet":
      return player.pickupRadius < 2.5;
    case "berserker":
      return player.berserker < 0.75;
    case "war_banner":
      return raid.teamPowerStacks < TEAM_POWER_MAX_STACKS;
    case "battle_rhythm":
      return raid.teamCooldownStacks < TEAM_COOLDOWN_MAX_STACKS;
    case "campfire":
      return raid.teamMaxHpStacks < TEAM_HP_MAX_STACKS;
    case "marching_tune":
      return raid.teamMoveSpeedStacks < TEAM_MOVE_MAX_STACKS;
    default:
      return false;
  }
}
